/**
 * catalogoApi.js - Scriptus Pharma Partner
 * Service de API para Catálogo de Produtos
 * 
 * ✅ Comunicação centralizada com Backend
 * ✅ Headers com autenticação
 * ✅ Tratamento de erros padronizado
 * ✅ Rate limiting client-side
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.scriptusrxia.com.br';

// ============================================================================
// RATE LIMITER SIMPLES
// ============================================================================
const rateLimiter = {
  requests: new Map(),
  
  checkLimit(key, maxRequests = 30, windowMs = 60000) {
    const now = Date.now();
    const windowStart = now - windowMs;
    
    if (!this.requests.has(key)) {
      this.requests.set(key, []);
    }
    
    const timestamps = this.requests.get(key).filter(t => t > windowStart);
    
    if (timestamps.length >= maxRequests) {
      throw new Error('Muitas requisições. Aguarde um momento.');
    }
    
    timestamps.push(now);
    this.requests.set(key, timestamps);
    return true;
  },
  
  clear(key) {
    if (key) {
      this.requests.delete(key);
    } else {
      this.requests.clear();
    }
  }
};

// ============================================================================
// SANITIZAÇÃO
// ============================================================================
const sanitizeString = (str, maxLength = 200) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .substring(0, maxLength)
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[<>]/g, '');   // Remove < >
};

const sanitizeProduto = (dados) => {
  return {
    nome: sanitizeString(dados.nome, 200),
    principioAtivo: sanitizeString(dados.principioAtivo, 100),
    concentracao: sanitizeString(dados.concentracao, 100),
    formaFarmaceutica: sanitizeString(dados.formaFarmaceutica, 50),
    preco: typeof dados.preco === 'number' ? Math.max(0, dados.preco) : 0,
    categoria: sanitizeString(dados.categoria, 50),
    codigoEan: sanitizeString(dados.codigoEan, 14).replace(/\D/g, ''),
    disponivel: Boolean(dados.disponivel),
    observacoes: sanitizeString(dados.observacoes, 500)
  };
};

// ============================================================================
// API SERVICE
// ============================================================================
const catalogoApi = {
  // Headers com autenticação
  getHeaders: async (auth) => {
    if (!auth?.currentUser) {
      throw new Error('Usuário não autenticado');
    }
    const token = await auth.currentUser.getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  },

  // ============================================================================
  // CRUD DE PRODUTOS
  // ============================================================================

  /**
   * Listar produtos da farmácia
   * @param {Object} auth - Firebase auth
   * @param {Object} filtros - Filtros opcionais { categoria, disponivel, busca }
   * @returns {Promise<{success: boolean, produtos: Array, stats?: Object}>}
   */
  listar: async (auth, filtros = {}) => {
    try {
      rateLimiter.checkLimit(`listar_${auth.currentUser?.uid}`, 30, 60000);
      
      const headers = await catalogoApi.getHeaders(auth);
      const params = new URLSearchParams();
      
      if (filtros.categoria) params.append('categoria', filtros.categoria);
      if (filtros.disponivel !== undefined) params.append('disponivel', filtros.disponivel);
      if (filtros.busca) params.append('busca', sanitizeString(filtros.busca, 100));
      if (filtros.limit) params.append('limit', Math.min(filtros.limit, 100));
      if (filtros.offset) params.append('offset', filtros.offset);
      
      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/v1/catalogo/produtos${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || 'Erro ao carregar produtos');
      }
      
      return response.json();
    } catch (error) {
      console.error('[catalogoApi.listar]', error);
      throw error;
    }
  },

  /**
   * Buscar produto por ID
   * @param {Object} auth - Firebase auth
   * @param {string} id - ID do produto
   * @returns {Promise<{success: boolean, produto: Object}>}
   */
  buscarPorId: async (auth, id) => {
    try {
      if (!id || typeof id !== 'string') {
        throw new Error('ID do produto inválido');
      }
      
      rateLimiter.checkLimit(`buscar_${auth.currentUser?.uid}`, 50, 60000);
      
      const headers = await catalogoApi.getHeaders(auth);
      const response = await fetch(`${API_BASE_URL}/api/v1/catalogo/produtos/${id}`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || 'Produto não encontrado');
      }
      
      return response.json();
    } catch (error) {
      console.error('[catalogoApi.buscarPorId]', error);
      throw error;
    }
  },

  /**
   * Criar novo produto
   * @param {Object} auth - Firebase auth
   * @param {Object} dados - Dados do produto
   * @returns {Promise<{success: boolean, produto: Object, produtoId: string}>}
   */
  criar: async (auth, dados) => {
    try {
      rateLimiter.checkLimit(`criar_${auth.currentUser?.uid}`, 10, 60000);
      
      const dadosSanitizados = sanitizeProduto(dados);
      
      // Validações
      if (!dadosSanitizados.nome || dadosSanitizados.nome.length < 2) {
        throw new Error('Nome do medicamento é obrigatório (mínimo 2 caracteres)');
      }
      
      if (dadosSanitizados.preco <= 0) {
        throw new Error('Preço deve ser maior que zero');
      }
      
      const headers = await catalogoApi.getHeaders(auth);
      const response = await fetch(`${API_BASE_URL}/api/v1/catalogo/produtos`, {
        method: 'POST',
        headers,
        body: JSON.stringify(dadosSanitizados)
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || 'Erro ao cadastrar produto');
      }
      
      return response.json();
    } catch (error) {
      console.error('[catalogoApi.criar]', error);
      throw error;
    }
  },

  /**
   * Atualizar produto existente
   * @param {Object} auth - Firebase auth
   * @param {string} id - ID do produto
   * @param {Object} dados - Dados atualizados
   * @returns {Promise<{success: boolean, produto: Object}>}
   */
  atualizar: async (auth, id, dados) => {
    try {
      if (!id || typeof id !== 'string') {
        throw new Error('ID do produto inválido');
      }
      
      rateLimiter.checkLimit(`atualizar_${auth.currentUser?.uid}`, 20, 60000);
      
      const dadosSanitizados = sanitizeProduto(dados);
      
      // Validações
      if (!dadosSanitizados.nome || dadosSanitizados.nome.length < 2) {
        throw new Error('Nome do medicamento é obrigatório (mínimo 2 caracteres)');
      }
      
      const headers = await catalogoApi.getHeaders(auth);
      const response = await fetch(`${API_BASE_URL}/api/v1/catalogo/produtos/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(dadosSanitizados)
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || 'Erro ao atualizar produto');
      }
      
      return response.json();
    } catch (error) {
      console.error('[catalogoApi.atualizar]', error);
      throw error;
    }
  },

  /**
   * Remover produto
   * @param {Object} auth - Firebase auth
   * @param {string} id - ID do produto
   * @returns {Promise<{success: boolean}>}
   */
  remover: async (auth, id) => {
    try {
      if (!id || typeof id !== 'string') {
        throw new Error('ID do produto inválido');
      }
      
      rateLimiter.checkLimit(`remover_${auth.currentUser?.uid}`, 10, 60000);
      
      const headers = await catalogoApi.getHeaders(auth);
      const response = await fetch(`${API_BASE_URL}/api/v1/catalogo/produtos/${id}`, {
        method: 'DELETE',
        headers
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || 'Erro ao remover produto');
      }
      
      return { success: true };
    } catch (error) {
      console.error('[catalogoApi.remover]', error);
      throw error;
    }
  },

  /**
   * Toggle disponibilidade do produto
   * @param {Object} auth - Firebase auth
   * @param {string} id - ID do produto
   * @returns {Promise<{success: boolean, disponivel: boolean}>}
   */
  toggleDisponibilidade: async (auth, id) => {
    try {
      if (!id || typeof id !== 'string') {
        throw new Error('ID do produto inválido');
      }
      
      rateLimiter.checkLimit(`toggle_${auth.currentUser?.uid}`, 30, 60000);
      
      const headers = await catalogoApi.getHeaders(auth);
      const response = await fetch(`${API_BASE_URL}/api/v1/catalogo/produtos/${id}/toggle`, {
        method: 'PATCH',
        headers
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || 'Erro ao atualizar disponibilidade');
      }
      
      return response.json();
    } catch (error) {
      console.error('[catalogoApi.toggleDisponibilidade]', error);
      throw error;
    }
  },

  /**
   * Reordenar produtos (persistir ordem)
   * @param {Object} auth - Firebase auth
   * @param {Array<string>} ordem - Array de IDs na nova ordem
   * @returns {Promise<{success: boolean}>}
   */
  reordenar: async (auth, ordem) => {
    try {
      if (!Array.isArray(ordem) || ordem.length === 0) {
        throw new Error('Ordem inválida');
      }
      
      rateLimiter.checkLimit(`reordenar_${auth.currentUser?.uid}`, 10, 60000);
      
      const headers = await catalogoApi.getHeaders(auth);
      const response = await fetch(`${API_BASE_URL}/api/v1/catalogo/produtos/reordenar`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ ordem })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || 'Erro ao reordenar produtos');
      }
      
      return response.json();
    } catch (error) {
      console.error('[catalogoApi.reordenar]', error);
      throw error;
    }
  },

  // ============================================================================
  // ESTATÍSTICAS
  // ============================================================================

  /**
   * Buscar estatísticas do catálogo
   * @param {Object} auth - Firebase auth
   * @returns {Promise<{success: boolean, stats: Object}>}
   */
  stats: async (auth) => {
    try {
      rateLimiter.checkLimit(`stats_${auth.currentUser?.uid}`, 20, 60000);
      
      const headers = await catalogoApi.getHeaders(auth);
      const response = await fetch(`${API_BASE_URL}/api/v1/catalogo/stats`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        console.warn('[catalogoApi.stats] Falha ao buscar estatísticas');
        return { success: false, stats: null };
      }
      
      return response.json();
    } catch (error) {
      console.error('[catalogoApi.stats]', error);
      return { success: false, stats: null };
    }
  },

  // ============================================================================
  // BUSCA PARA MÉDICOS (público/autenticado)
  // ============================================================================

  /**
   * Buscar produtos de todas as farmácias Partner (para médicos)
   * @param {Object} auth - Firebase auth do médico
   * @param {Object} params - { termo, lat, lng, raio }
   * @returns {Promise<{success: boolean, produtos: Array}>}
   */
  buscarParaMedico: async (auth, params = {}) => {
    try {
      const headers = await catalogoApi.getHeaders(auth);
      const queryParams = new URLSearchParams();
      
      if (params.termo) queryParams.append('termo', sanitizeString(params.termo, 100));
      if (params.lat) queryParams.append('lat', params.lat);
      if (params.lng) queryParams.append('lng', params.lng);
      if (params.raio) queryParams.append('raio', params.raio);
      
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/api/v1/catalogo/buscar${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        throw new Error('Erro ao buscar produtos');
      }
      
      return response.json();
    } catch (error) {
      console.error('[catalogoApi.buscarParaMedico]', error);
      throw error;
    }
  }
};

// ============================================================================
// EXPORTS
// ============================================================================
export { catalogoApi, sanitizeString, sanitizeProduto, rateLimiter };
export default catalogoApi;